"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, CircleCheck as CheckCircle2, ShieldCheck, Sparkles, TrendingUp, Clock } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
export default function Hero() {
  const prefersReduced = useReducedMotion();
  const benefits = [{
    text: "Direct 1-on-1 access to Yassine"
  }, {
    text: "No junior staff or agency layers"
  }, {
    text: "Transparent plans built for your budget"
  }];
  return <section id="hero" data-nav="light" className="relative overflow-hidden bg-background text-foreground pt-28 pb-16 md:pt-36 md:pb-24 lg:pt-40 lg:pb-32">
      {/* Subtle geometric background accents */}
      <div aria-hidden="true" className="pointer-events-none absolute -top-24 right-0 -z-10 h-96 w-96 rounded-full bg-secondary/60 blur-3xl opacity-60" />
      <div aria-hidden="true" className="pointer-events-none absolute top-1/2 left-0 -z-10 h-72 w-72 rounded-full bg-primary/5 blur-3xl" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          {/* Main Copy Column */}
          <div className="lg:col-span-7 flex flex-col items-start text-left">
            <motion.div initial={prefersReduced ? false : {
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.4
          }} className="mb-4">
              <Badge variant="secondary" className="px-3.5 py-1 text-xs sm:text-sm font-medium rounded-full border border-border/80 flex items-center gap-1.5">
                <Sparkles className="size-3.5 text-accent" />
                <span>Freelance SEO consultancy for ambitious businesses</span>
              </Badge>
            </motion.div>

            <motion.h1 initial={prefersReduced ? false : {
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground leading-[1.12]">
              Professional SEO consultant for sustainable search growth
            </motion.h1>

            <motion.p initial={prefersReduced ? false : {
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="mt-6 text-base sm:text-lg text-muted-foreground max-w-2xl leading-relaxed font-sans">
              I help small and growing businesses earn lasting organic visibility across the UK and
              beyond. You work directly with me—every audit, strategy session, and technical
              fix is executed by a senior SEO specialist, without agency markups or hidden fees.
            </motion.p>

            {/* Direct Value Checkmarks */}
            <motion.ul initial={prefersReduced ? false : {
            opacity: 0,
            y: 14
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.28
          }} className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3 w-full max-w-2xl pt-2">
              {benefits.map((benefit, i) => <li key={benefit.text} data-index={i} className="flex items-center gap-2 text-xs sm:text-sm text-foreground/90 font-medium">
                  <CheckCircle2 className="size-4 text-primary shrink-0" />
                  <span>{benefit.text}</span>
                </li>)}
            </motion.ul>

            {/* CTAs */}
            <motion.div initial={prefersReduced ? false : {
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.35
          }} className="mt-8 sm:mt-10 flex flex-col sm:flex-row items-stretch sm:items-center gap-3.5 sm:gap-4 w-full sm:w-auto">
              <Button asChild size="lg" className="rounded-md font-medium text-sm sm:text-base px-6 h-12 shadow-sm">
                <Link to="/ContactPage">
                  <span>Book a Free SEO Consultation</span>
                  <ArrowRight className="size-4 ml-1.5" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="rounded-md font-medium text-sm sm:text-base px-6 h-12 border-border hover:bg-muted">
                <Link to="/CaseStudiesPage">
                  <span>Explore My Case Studies</span>
                </Link>
              </Button>
            </motion.div>

            {/* Micro Trust Banner */}
            <div className="mt-8 pt-6 border-t border-border/70 flex flex-wrap items-center gap-y-2 gap-x-6 text-xs text-muted-foreground">
              <div className="flex items-center gap-1.5">
                <ShieldCheck className="size-4 text-primary" />
                <span>Zero long-term lock-in contracts</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Clock className="size-4 text-primary" />
                <span>Response within 24 business hours</span>
              </div>
              <div className="flex items-center gap-1.5">
                <TrendingUp className="size-4 text-primary" />
                <span>Hands-on technical implementation</span>
              </div>
            </div>
          </div>

          {/* Consultant Profile Visual Column */}
          <div className="lg:col-span-5 relative flex justify-center lg:justify-end">
            <motion.div initial={prefersReduced ? false : {
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.55,
            delay: 0.2
          }} className="relative w-full max-w-md lg:max-w-none">
              {/* Backing decorative glow */}
              <div aria-hidden="true" className="absolute inset-0 translate-x-3 translate-y-3 rounded-2xl bg-secondary/70 -z-10 border border-border" />

              {/* Main Profile Frame */}
              <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-lg">
                <div className="relative aspect-4/5 w-full bg-muted">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_seo_consultant.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_seo_consultant.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_seo_consultant.webp-wvc-sizes" alt="Yassine Essaadi, independent freelance SEO consultant" loading="lazy" decoding="async" className="h-full w-full object-cover object-center" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

                  {/* Profile overlay badge */}
                  <div className="absolute bottom-4 left-4 right-4 p-3.5 rounded-xl bg-background/95 backdrop-blur-sm border border-border text-foreground shadow-sm">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-foreground">Yassine Essaadi</p>
                        <p className="text-xs text-muted-foreground">Freelance SEO Strategist &amp; Consultant</p>
                      </div>
                      <div className="text-right">
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-primary text-primary-foreground">
                          Direct Access
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Practical value footer strip */}
                <div className="p-4 sm:p-5 bg-card border-t border-border flex items-center justify-between text-xs sm:text-sm text-foreground">
                  <div>
                    <span className="text-muted-foreground block text-[11px] uppercase tracking-wider">Independent Partner</span>
                    <span className="font-medium text-foreground">YE Consultancy</span>
                  </div>
                  <div className="text-right">
                    <span className="text-muted-foreground block text-[11px] uppercase tracking-wider">Location</span>
                    <span className="font-medium text-foreground">Swindon &amp; Nationwide UK</span>
                  </div>
                </div>
              </div>

              {/* Float badge */}
              <div className="absolute -top-3 -left-3 hidden sm:flex items-center gap-2 rounded-lg bg-background border border-border px-3.5 py-2 shadow-md">
                <span className="flex h-2.5 w-2.5 rounded-full bg-accent" />
                <span className="text-xs font-semibold text-foreground">Accepting Q2 SEO audits</span>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}